import { bufferedMessage, IMAXAError, IMAXAServerMessage, IMAXAClientMessage, IMAXAMessageCallback, Cookie, IMAXAErrorCodes } from './IMAXAProtocol'
import { Constants, virtualWheel } from '@core/Utils/Constants';
import { IMAXAPilotSeat } from './IMAXAPilotSeat';
import { Client } from '@core/Client';
import { RenderListener } from '@core/Logic/RenderListener';
import { MainScene } from '@core/Scenes/MainScene';
import { GetParameterByName } from '@core/Utils/HelperFunction';

export class IMAXAClient implements RenderListener {
	mainSceneReady: boolean;
	mainSceneActive: boolean;
	mainScene: MainScene;

	public pilotSeat: IMAXAPilotSeat;
	public yserverReady: boolean = false;

	private webSocket: WebSocket
	private startingServerTS: number = 0;
	private startingClientTS: number = 0;
	// Must increment it with every request
	private contextID: number = 1;
	private messageID: number = 0;
	private messageBuffer: Map<Number, Map<number, bufferedMessage>> = new Map<number, Map<number, bufferedMessage>>();
	private timeoutTime: number = 1500;
	private questionBuffer: Array<IMAXAServerMessage> = new Array<IMAXAServerMessage>();
	private doNotRetryReconnection: boolean = false;
	private reconnectTimeout = null;

	OnInit: IMAXAMessageCallback;
	OnError: (event: string, imaxaErrorPacket?: IMAXAError) => void;
	OnServerEvent: (eventName: string, eventData: any) => void;
	OnQuestion: (question: IMAXAServerMessage) => void;

	constructor(protected address: string) {
		//Listener for when MainScene is ready to render and can display things such as questions/input and such
		Client.game.events.addListener(Constants.EVENTS.MAIN_SCENE_READY, this.OnMainSceneReady, this);

		window.onerror = (event: any, source: string, lineno: number, colno: number, error: any) => {
			console.log(source + " at " + lineno)
			let dataObject = {
				"verbosity": "Error",
				"msg": JSON.stringify({
					event: event,
					source: source,
					lineno: lineno,
					colno: colno,
					error: error
				})
			}
			this.SendRequest("log", dataObject, () => {
				console.log("sent")
			})
			return false
		}

		if (!Client.slot)
			window.addEventListener("beforeunload", function (e) {
				if (Client.logic.BetSum > 0) {
					var confirmationMessage = "You have an active bet, are you sure you want to leave?";
					(e || window.event).returnValue = confirmationMessage;
					return confirmationMessage;
				}
				else
					return null;
			});

		window.onclose = () => {
			this.webSocket.onclose = function () { }; // disable onclose handler first
			this.webSocket.close();
			this.DisconnectSocket();
		};

		window.addEventListener('unhandledrejection', function (e) {
			console.log(e)
		})

		if (virtualWheel)
			this.timeoutTime = 5000;
		else
			this.timeoutTime = 1500;
	}

	CreateNewSocket(): void {
		this.webSocket = new WebSocket(this.address);
		this.webSocket.onmessage = this.onMessage.bind(this);
		this.webSocket.onerror = this.onSocketError.bind(this);
		this.webSocket.onclose = this.onClose.bind(this);
	}

	public OnMainSceneReady(mainScene: MainScene): void {
		this.mainSceneReady = true;
		this.mainScene = mainScene;

		this.questionBuffer.forEach(question => {
			this.processQuestion(question);
		});
		this.questionBuffer = null;
		Client.game.events.removeListener(Constants.EVENTS.MAIN_SCENE_READY);

		if (this.pilotSeat) {
			this.pilotSeat.AssignMainScene(this.mainScene);
			this.pilotSeat.SendAllAvailableRemoteActions();
		}
	}

	OnMainSceneActive(mainScene: MainScene): void {
		this.mainSceneActive = true;
	}

	get Connected(): boolean {
		return this.webSocket.readyState == 1
	}

	DisconnectSocket(): void {
		if (this.pilotSeat)
			this.pilotSeat.haveSentInitialMessage = false;

		this.webSocket.close();
	}

	public onOpen(handler: (ev: Event) => any): void {
		this.webSocket.onopen = handler;
	}

	public AnswerQuestion(data: any): void {
		this.Send("response", data, (ok: boolean, response: any) => {
			if (!ok) {
				console.log("Server responded with error on question: " + JSON.stringify(response));
			} else {
				this.RemoteActionsHaveChanged();
			}
		})
	}

	public RemoteActionsHaveChanged(): void {
		if (this.pilotSeat)
			this.pilotSeat.SendRemoteActionChanges();
	}

	private onMessage(wsmsg: MessageEvent): any {
		let m = JSON.parse(wsmsg.data);
		//console.log("GOT PACKET : " + JSON.stringify(m));

		let baseMsg = new IMAXAServerMessage(m);
		//baseMsg.receivedTS = wsmsg.timeStamp;

		this.startingServerTS = m.sentTS;
		this.startingClientTS = Date.now();

		if (baseMsg.contextID % 2 == 1) { // client initiated messages
			let allMessagesUnderContext = this.messageBuffer.get(baseMsg.contextID);
			if (allMessagesUnderContext) {
				allMessagesUnderContext.forEach((msg: bufferedMessage) => {
					clearTimeout(msg.callbackTimeoutID);
					if (msg.callback)
						msg.callback(baseMsg.type != Constants.YSERVER_MESSAGE_TYPES.ERROR, baseMsg);
				});
				this.messageBuffer.delete(baseMsg.contextID);
			}

			return;
		} 
		/*else if (baseMsg.contextID == 0 && baseMsg.type == Constants.YSERVER_MESSAGE_TYPES.ERROR) // error on first message, treat as init "response"
		{
			this.handleInit(baseMsg, true);
			return;
		}
		*/

		// server initiated messages
		switch (baseMsg.type) {
			case Constants.YSERVER_MESSAGE_TYPES.EVENT:
				if (baseMsg.payload.name == "authenticated") {
					this.handleInit(baseMsg, false);

					//Init packet has pilot seat enabled
					if (baseMsg.payload.data.pilotSeat && Client.slot) {
						if (!this.pilotSeat) {
							this.pilotSeat = new IMAXAPilotSeat();
						}

						if (this.mainSceneReady && this.mainScene) {
							this.pilotSeat.AssignMainScene(this.mainScene);
							this.pilotSeat.SendAllAvailableRemoteActions();
						}
					}
				}
				else if (this.pilotSeat && baseMsg.payload.name == "action") {
					this.pilotSeat.HandleRemoteAction(baseMsg.payload.data);
				}
				else if (this.OnServerEvent) {
					this.OnServerEvent(baseMsg.payload.name, baseMsg.payload.data);
				}
				break;
			case Constants.YSERVER_MESSAGE_TYPES.PING:
				this.sendInternal(Constants.CLIENT_MESSAGE_TYPES.PONG, baseMsg.contextID, { received: baseMsg.sentTS })
				break;
			case Constants.YSERVER_MESSAGE_TYPES.ERROR:
				console.log("ERROR:" + baseMsg.payload.message);

				//If a more critical error occured, do not spam the server with reconnects
				if (baseMsg.payload.code == IMAXAErrorCodes.ERR_UNAUTHORIZED ||
					baseMsg.payload.code == IMAXAErrorCodes.ERR_BANNED ) {
					this.doNotRetryReconnection = true;
					setTimeout(() => {
						try {
							let retVal = GetParameterByName("ret")
							if (retVal && retVal.length > 1)
								window.open(retVal, "_self")
							else if (window.history.length > 2)
								window.history.back()
						}
						catch (e) { }
					}, 3000)

					if (this.reconnectTimeout) {
						clearTimeout(this.reconnectTimeout);
						this.reconnectTimeout = null;
					}
				}
				this.OnError(baseMsg.payload.message, new IMAXAError(baseMsg));
				break;
			case Constants.YSERVER_MESSAGE_TYPES.INIT:
				this.yserverReady = true;

				//send all prebuffered messages
				this.messageBuffer.forEach(allMessagesUnderBuffer => {
					allMessagesUnderBuffer.forEach(bufferedMessage => {
						if (bufferedMessage.message.sentTS == 0 || bufferedMessage.message.sentTS == null) {
							//set timeout the moment the message is sent out
							bufferedMessage.callbackTimeoutID = setTimeout(function () {
								if (bufferedMessage.callback)
									bufferedMessage.callback(false, null);
								let allMessagesUnderContext = this.messageBuffer.get(bufferedMessage.message.contextID);
								if (allMessagesUnderContext) {
									allMessagesUnderContext.delete(bufferedMessage.uniqueID)
								}
							}.bind(this), this.timeoutTime)

							bufferedMessage.message.sentTS = Math.round(this.getServerTimestamp());

							this.webSocket.send(JSON.stringify(bufferedMessage.message))
							//console.log("SEND : " + JSON.stringify(bufferedMessage.message));
						}
					});
				});
				break;
			case Constants.YSERVER_MESSAGE_TYPES.QUESTION:
				if (this.mainSceneReady) {
					this.processQuestion(baseMsg);
				}
				else {
					this.questionBuffer.push(baseMsg);
				}
				break;
			case Constants.YSERVER_MESSAGE_TYPES.COOKIE:
				let cookie = baseMsg.payload as Cookie;
				let cookieStr = `${cookie.name}=${cookie.value}`;
				for (let optI = 0; optI < cookie.options.length; optI++) {
					cookieStr += "; " + cookie.options[optI];
				}
				try {
					document.cookie = cookieStr;
				} catch (e) { console.log(e) }
				break;
			default:
				break;
		}
	}

	private processQuestion(message: IMAXAServerMessage): void {
		this.OnQuestion(message);
	}

	private handleInit(msg: IMAXAServerMessage, error: boolean) {
		if (error) {
			console.error("Error connecting to YServer!");
			console.error(msg.payload);
			this.OnInit(false, msg);
			this.yserverReady = false;
		} else {
			console.log("Got YServer init!");
			console.log(msg.payload.data);
			this.OnInit(true, msg);
			clearTimeout(this.reconnectTimeout);
		}
	}

	private onSocketError(error?: Event): any {
		console.error(error)
		this.startingServerTS = 0;
		this.OnError("Connection error");
		this.yserverReady = false;
		this.RetryConnection();
	}

	public onClose(): void {
		this.yserverReady = false;
		this.RetryConnection();
	}

	RetryConnection(): void {
		console.log("retrying connection...");
		this.reconnectTimeout = setTimeout(() => {
			if (this.doNotRetryReconnection) {
				clearTimeout(this.reconnectTimeout);
				return;
			}
			console.log("creating new socket...");
			this.CreateNewSocket();
		}, Constants.CONNECTION_RETRY_INTERVAL);
	}

	Send(type: string, data: any, _callback?: IMAXAMessageCallback): void {
		this.sendInternal(type, this.contextID += 2, data, _callback);
	}

	SendRequest(requestName: string, context: any, _callback?: IMAXAMessageCallback): number {
		this.sendInternal(Constants.CLIENT_MESSAGE_TYPES.REQUEST, this.contextID += 2, { request: requestName, data: context }, _callback)
		return this.contextID;
	}

	// private async send(data: any): Promise<void> {
	private sendInternal(type: string, contextID: number, data: any, _callback?: IMAXAMessageCallback): void {

		let uniqueIDForThisMessage = this.messageID++;
		//add message to buffer;
		let msg: bufferedMessage = {
			uniqueID: uniqueIDForThisMessage,
			message: new IMAXAClientMessage({
				type: type,
				contextID: contextID,
				sentTS: 0,
				payload: data
			}),
			callback: _callback,
			callbackTimeoutID: null
		};

		if (this.messageBuffer.has(contextID)) {
			this.messageBuffer.get(contextID).set(msg.uniqueID, msg);
		}
		else {
			let savedMessage: Map<number, bufferedMessage> = new Map<number, bufferedMessage>();
			savedMessage.set(msg.uniqueID, msg);
			this.messageBuffer.set(contextID, savedMessage);
		}

		if (this.yserverReady) {

			msg.callbackTimeoutID = setTimeout(function () {
				if (msg.callback)
					msg.callback(false, null);
				let allMessagesUnderContext = this.messageBuffer.get(msg.message.contextID);
				if (allMessagesUnderContext) {
					allMessagesUnderContext.delete(msg.uniqueID)
				}
			}.bind(this), this.timeoutTime)

			msg.message.sentTS = Math.round(this.getServerTimestamp());

			this.webSocket.send(JSON.stringify(msg.message))
			//console.log("SEND : " + JSON.stringify(msg.message));
		}
	}

	private getServerTimestamp(): number {
		return (Date.now() - this.startingClientTS) + this.startingServerTS;
	}
}